<template>
  <div v-if="isVisible" class="modal-overlay" @click.self="closeModal">
    <div class="modal-shell">
      <div class="modal-header">
        <div>
          <h2 class="modal-title">{{ editMode ? 'Редактировать проект' : 'Добавить проект' }}</h2>
          <p class="modal-subtitle">
            {{ canManageParticipants ? 'Заполните основные данные и добавьте участников' : 'Заполните основные данные, участник будет добавлен автоматически' }}
          </p>
        </div>
        <button type="button" class="button button-outline !h-8 !w-8 !px-0" @click="closeModal" aria-label="Закрыть">
          ✕
        </button>
      </div>

      <form class="modal-form" @submit.prevent="editMode ? submitEditProject() : addProject()">
        <section class="section-block">
          <h3 class="section-title">Основное</h3>
          <div class="main-grid">
            <FormInput
              v-model="newProject.title"
              label="Название проекта"
              placeholder="Название проекта"
              required
              width="100%"
              containerClasses="!mb-0"
            />
            <FormInput
              v-model="newProject.date"
              type="date"
              label="Дата"
              required
              width="100%"
              containerClasses="!mb-0"
            />
            <FormInput
              v-model="newProject.category"
              type="select"
              label="Категория"
              :options="[
                { value: 'COMMERCIAL', label: 'COMMERCIAL' },
                { value: 'BROADCAST', label: 'BROADCAST' },
                { value: '3D', label: '3D' },
                { value: 'SOUND', label: 'SOUND' },
                { value: 'DESIGN', label: 'DESIGN' },
                { value: 'ABOUT', label: 'ABOUT' }
              ]"
              placeholder="Выберите категорию"
              required
              width="100%"
              containerClasses="!mb-0"
            />
            <FormInput
              v-if="canManagePin"
              v-model="newProject.pin"
              type="number"
              label="Порядок (pin)"
              placeholder="Чем меньше, тем выше"
              width="100%"
              containerClasses="!mb-0"
            />
            <div class="md:col-span-2">
              <label class="mb-1 block text-sm font-medium text-foreground">Описание</label>
              <textarea
                v-model="newProject.content"
                class="input-field modal-textarea"
                placeholder="Описание проекта"
              />
            </div>
          </div>
        </section>

        <section class="section-block">
          <h3 class="section-title">Медиа</h3>
          <div class="media-grid">
            <div class="media-upload-card">
              <FileUpload
                label="Обложка"
                fileType="cover"
                :onUpload="uploadCoverFile"
                @file-uploaded="handleCoverUploaded"
              />
            </div>
            <div class="media-upload-card">
              <FileUpload
                label="Короткий ролик"
                fileType="short_video"
                :onUpload="uploadShortVideoFile"
                @file-uploaded="handleShortVideoUploaded"
              />
            </div>
            <div class="media-upload-card">
              <FileUpload
                label="Основное видео"
                fileType="main_video"
                :onUpload="uploadMainVideoFile"
                @file-uploaded="handleMainVideoUploaded"
              />
            </div>
            <div class="media-upload-card">
              <FileUpload
                label="Бэкстейдж видео"
                fileType="backstage_video"
                :onUpload="uploadBackstageVideoFile"
                @file-uploaded="handleBackstageVideoUploaded"
              />
            </div>
          </div>
        </section>

        <section class="section-block">
          <BackstageGalleryInline
            title="Backstage галерея проекта"
            context="project"
            :context-id="editMode ? newProject.id : null"
            :allow-reorder="editMode"
            :pending-items="pendingProjectBackstageImages"
            @update:pendingItems="pendingProjectBackstageImages = $event"
          />
        </section>

        <section class="section-block">
          <div class="participants-header">
            <h3 class="section-title m-0">Участники</h3>
            <button
              v-if="canManageParticipants"
              type="button"
              class="button button-outline !h-8 !px-3 !py-0 text-xs"
              @click="addParticipant"
            >
              Добавить участника
            </button>
          </div>

          <div v-if="!canManageParticipants" class="participants-empty">
            Обычные пользователи не могут добавлять участников. Вы будете добавлены в проект автоматически.
          </div>

          <template v-else>
            <div v-if="newProject.participants.length === 0" class="participants-empty">
              Добавьте хотя бы одного участника
            </div>

            <div v-for="(participant, index) in newProject.participants" :key="index" class="participant-row">
              <select class="input-field participant-user-select" v-model="participant.user_id" required>
                <option value="" disabled>Выберите участника</option>
                <option v-for="user in localUsers" :key="user.id" :value="user.id">{{ user.title }}</option>
              </select>

              <FormAutocomplete
                v-model="participant.roleSearch"
                :options="roles"
                placeholder="Начните вводить роль"
                width="100%"
                @update:modelValue="val => selectRole(index, val)"
              />

              <button type="button" class="button button-danger !h-9 !px-3 !py-0 text-xs" @click="removeParticipant(index)">
                Удалить
              </button>
            </div>
          </template>
        </section>

        <div class="modal-actions">
          <button type="button" class="button button-outline" @click="closeModal">Отмена</button>
          <button type="submit" class="button">{{ editMode ? 'Сохранить изменения' : 'Добавить проект' }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { addProjectApi, getUsersApi, fetchPublicUsersApi, uploadFile, createBackstageImage } from '@/services/api';
import { useToast } from 'vue-toastification';
import FormInput from './FormInput.vue';
import FormAutocomplete from './FormAutocomplete.vue';
import FileUpload from './FileUpload.vue';
import BackstageGalleryInline from './BackstageGalleryInline.vue';

export default {
  name: 'ProjectModal',
  props: {
    isVisible: {
      type: Boolean,
      required: true
    },
    projectToEdit: {
      type: Object,
      default: null
    },
    editMode: {
      type: Boolean,
      default: false
    },
    users: {
      type: Array,
      default: () => []
    },
    currentUser: {
      type: Object,
      default: () => ({})
    },
    canManagePin: {
      type: Boolean,
      default: true
    },
    canManageParticipants: {
      type: Boolean,
      default: true
    }
  },
  components: {
    FormInput,
    FormAutocomplete,
    FileUpload,
    BackstageGalleryInline
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      localUsers: [],
      roles: [
        "Режиссёр",
        "Продюсер",
        "Сценарист",
        "Оператор-постановщик",
        "Художник-постановщик",
        "Второй режиссёр",
        "Звукорежиссёр",
        "Художник по костюмам",
        "Художник по гриму",
        "Художник по реквизиту",
        "Гафер",
        "Оператор",
        "Скрипт-супервайзер",
        "Помощник режиссёра",
        "Осветитель",
        "Актёр",
        "Каскадёр",
        "Режиссёр монтажа",
        "Монтажёр",
        "Колорист",
        "Супервайзер визуальных эффектов (VFX)",
        "Композитинг-артист",
        "3D-художник",
        "Аниматор",
        "Режиссёр по работе с актёрами",
        "Кастинг-директор",
        "Директор фильма",
        "Администратор площадки",
        "Локационный менеджер",
        "Постановщик трюков",
        "Пиротехник",
        "Специалист по спецэффектам (SFX)",
        "Дольщик (Steadicam-оператор)",
        "Оператор крана",
        "Оператор дрона",
        "Подводный оператор",
        "Бум-оператор (звукоуловитель)",
        "Микрофонный оператор",
        "Диктор",
        "Композитор",
        "Саунд-дизайнер",
        "Фолей-артист (озвучка шумов)",
        "Редактор титров",
        "Субтитрёр",
        "Режиссёр дубляжа",
        "Переводчик",
        "Продюсер постпродакшна",
        "Технический директор",
        "Ассистент оператора",
        "Фотограф со съёмочной площадки",
        "Стилист",
        "Декоратор",
        "Бутафор",
        "Режиссёр клипов",
        "Режиссёр документального кино",
        "Режиссёр анимационного кино",
        "Сторибордист (художник раскадровки)",
        "Режиссёр прямого эфира",
        "Телевизионный продюсер",
        "Ведущий (TV/стримов)",
        "Спортивный комментатор",
        "Аналитик в эфире",
        "Видеоинженер",
        "Видеооператор в студии",
        "Оператор видеомикшера",
        "Графический оператор (визуализация в эфире)",
        "Супервайзер телетрансляции",
        "Техник телеметрии",
        "Специалист по хромакею",
        "Мультикам-оператор (многокамерная съёмка)",
        "Режиссёр повторов (в спорте)",
        "Оператор табло/счётчика",
        "Супервайзер медленных повторов (Slow Motion Operator)",
        "Звукорежиссёр прямого эфира",
        "Микрофонный ассистент (в студии/на поле)",
        "Светорежиссёр (в студии)",
        "Сет-дизайнер (оформление студии)",
        "Сценарист телепередач",
        "Редактор эфира",
        "Ассистент продюсера",
        "Координатор гостей в студии",
        "Суфлёр",
        "Техник по телекоммуникациям",
        "Специалист по потоковому вещанию (стримингу)"
      ],
      newProject: {
        title: '',
        date: '',
        category: '',
        content: '',
        pin: null,
        cover_id: '',
        short_video_id: '',
        backstage_video_id: '',
        main_video_id: '',
        participants: [{
          role: '', 
          user_id: '',
          roleSearch: '',
          showDropdown: false,
          filteredRoles: []
        }]
      },
      uploadStatus: {
        cover: { status: 'idle' },
        shortVideo: { status: 'idle' },
        mainVideo: { status: 'idle' },
        backstageVideo: { status: 'idle' },
      },
      pendingProjectBackstageImages: []
    };
  },
  async mounted() {
    if (this.canManageParticipants) {
      if (this.users && this.users.length) {
        this.localUsers = this.users;
      } else {
        this.localUsers = this.canManagePin
          ? await getUsersApi()
          : await fetchPublicUsersApi();
      }
      if (!this.canManagePin) {
        this.ensureCurrentUserInLocalUsers();
      }
    } else if (this.currentUser?.id) {
      this.localUsers = [{
        id: this.currentUser.id,
        title: this.currentUser.title || this.currentUser.username || 'Текущий пользователь'
      }];
      this.newProject.participants = this.buildDefaultParticipantsForCurrentUser();
    }
    this.disableScroll();
    if (this.editMode && this.projectToEdit) {
      this.initializeEditProject(this.projectToEdit);
    }
    if (!this.editMode && !this.canManagePin) {
      this.ensureCreatorDefaultParticipant();
    }
    if (this.canManageParticipants) {
      this.newProject.participants.forEach(p => {
        p.filteredRoles = [...this.roles];
      });
    }
  },
  beforeUnmount() {
    this.enableScroll();
  },
  methods: {
    ensureCurrentUserInLocalUsers() {
      if (!this.currentUser?.id) {
        return;
      }

      const currentUserExists = this.localUsers.some(
        user => String(user.id) === String(this.currentUser.id)
      );
      if (currentUserExists) {
        return;
      }

      this.localUsers.unshift({
        id: this.currentUser.id,
        title: this.currentUser.title || this.currentUser.username || 'Текущий пользователь'
      });
    },
    ensureCreatorDefaultParticipant() {
      const creatorId = this.currentUser?.id;
      if (!creatorId) {
        return;
      }

      const hasCreator = this.newProject.participants.some(
        participant => String(participant.user_id || '') === String(creatorId)
      );
      if (hasCreator) {
        return;
      }

      const firstEmptyParticipant = this.newProject.participants.find(
        participant => !participant.user_id
      );
      if (firstEmptyParticipant) {
        firstEmptyParticipant.user_id = creatorId;
        return;
      }

      this.newProject.participants.unshift({
        role: '',
        user_id: creatorId,
        roleSearch: '',
        showDropdown: false,
        filteredRoles: [...this.roles]
      });
    },
    buildDefaultParticipantsForCurrentUser() {
      if (!this.currentUser?.id) {
        return [];
      }
      return [{
        role: '',
        user_id: this.currentUser.id,
        roleSearch: '',
        showDropdown: false,
        filteredRoles: [...this.roles]
      }];
    },
    closeModal() {
      this.$emit('close');
    },
    initializeEditProject(project) {
      this.newProject = {
        id: project.id,
        title: project.title || '',
        date: project.date ? new Date(project.date).toISOString().split('T')[0] : '',
        category: project.category || '',
        content: project.content || '',
        pin: project.pin || null,
        cover_id: project.cover_id || '',
        short_video_id: project.short_video_id || '',
        backstage_video_id: project.backstage_video_id || '',
        main_video_id: project.main_video_id || '',
        participants: (project.users || []).map(userRole => ({
          role: userRole.role || '',
          user_id: userRole.user?.id || '',
          roleSearch: userRole.role || '',
          showDropdown: false,
          filteredRoles: [...this.roles]
        }))
      };
    },
    async addProject() {
      try {
        const restrictedParticipants = this.buildDefaultParticipantsForCurrentUser();
        const projectToSend = {
          ...this.newProject,
          participants: this.canManageParticipants
            ? this.newProject.participants.map(p => ({
              role: p.role,
              user_id: p.user_id
            }))
            : (restrictedParticipants.length
              ? restrictedParticipants.map(p => ({
                role: p.role,
                user_id: p.user_id
              }))
              : this.newProject.participants.map(p => ({
                role: p.role,
                user_id: p.user_id
              })))
        };

        const fieldsToRemove = ['cover_id', 'short_video_id', 'backstage_video_id', 'main_video_id'];
        fieldsToRemove.forEach(field => {
          if (projectToSend[field] === '') {
            delete projectToSend[field];
          }
        });

        if (!this.canManagePin || projectToSend.pin === null || projectToSend.pin === undefined) {
          delete projectToSend.pin;
        }

        const createdProject = await addProjectApi(projectToSend);
        if (createdProject?.id && this.pendingProjectBackstageImages.length) {
          const imagesToCreate = [...this.pendingProjectBackstageImages];
          this.pendingProjectBackstageImages = [];
          for (let index = 0; index < imagesToCreate.length; index += 1) {
            const item = imagesToCreate[index];
            await createBackstageImage({
              file_id: item.file_id,
              context: 'project',
              context_id: String(createdProject.id),
              position: index
            });
          }
        }
        this.$emit('project-added');
        this.closeModal();
      } catch (error) {
        // В том числе ловим ошибку "Для публикации проекта необходимо заполнить обязательные поля: ..."
        // и показываем её в тосте.
        console.error('Ошибка при создании проекта:', error);
        const message = error?.message || 'Ошибка при создании проекта';
        this.toast?.error?.(message);
      }
    },
    submitEditProject() {
      const updatedProject = {
        ...this.newProject,
        users: this.newProject.participants.map(p => ({
          role: p.role,
          user: this.localUsers.find(u => u.id === p.user_id) || {}
        }))
      };

      // Удаляем pin если он null или undefined
      if (updatedProject.pin === null || updatedProject.pin === undefined) {
        delete updatedProject.pin;
      }

      this.$emit('edit-project', updatedProject);
      this.closeModal();
    },
    addParticipant() {
      if (!this.canManageParticipants) {
        return;
      }
      this.newProject.participants.push({
        role: '',
        user_id: '',
        roleSearch: '',
        showDropdown: false,
        filteredRoles: [...this.roles]
      });
    },
    removeParticipant(index) {
      if (!this.canManageParticipants) {
        return;
      }
      this.newProject.participants.splice(index, 1);
    },
    openDropdown(index) {
      this.newProject.participants[index].showDropdown = true;
    },
    filterRoles(index) {
      const searchTerm = this.newProject.participants[index].roleSearch.toLowerCase();
      this.newProject.participants[index].filteredRoles = this.roles.filter(role =>
        role.toLowerCase().includes(searchTerm)
      );
      this.newProject.participants[index].showDropdown = true;
    },
    filteredRoles(index) {
      return this.newProject.participants[index].filteredRoles;
    },
    selectRole(index, role) {
      this.newProject.participants[index].role = role;
      this.newProject.participants[index].roleSearch = role;
      this.newProject.participants[index].showDropdown = false;
    },
    async uploadCoverFile(file, onProgress) {
      return await uploadFile(file, onProgress);
    },
    async uploadShortVideoFile(file, onProgress) {
      return await uploadFile(file, onProgress);
    },
    async uploadMainVideoFile(file, onProgress) {
      return await uploadFile(file, onProgress);
    },
    async uploadBackstageVideoFile(file, onProgress) {
      return await uploadFile(file, onProgress);
    },
    handleCoverUploaded(data) {
      this.newProject.cover_id = data.id;
      this.uploadStatus.cover.status = data.status;
    },
    handleShortVideoUploaded(data) {
      this.newProject.short_video_id = data.id;
      this.uploadStatus.shortVideo.status = data.status;
    },
    handleMainVideoUploaded(data) {
      this.newProject.main_video_id = data.id;
      this.uploadStatus.mainVideo.status = data.status;
    },
    handleBackstageVideoUploaded(data) {
      this.newProject.backstage_video_id = data.id;
      this.uploadStatus.backstageVideo.status = data.status;
    },
    disableScroll() {
      document.body.style.overflow = 'hidden';
    },
    enableScroll() {
      document.body.style.overflow = '';
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  @apply fixed inset-0 z-[1100] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm;
}

.modal-shell {
  @apply flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border bg-card text-card-foreground shadow-2xl;
}

.modal-header {
  @apply flex items-start justify-between gap-3 border-b px-5 py-4;
}

.modal-title {
  @apply m-0 text-lg font-semibold;
}

.modal-subtitle {
  @apply mt-1 text-sm text-muted-foreground;
}

.modal-form {
  @apply flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4;
}

.section-block {
  @apply rounded-lg border bg-background p-4;
}

.section-title {
  @apply mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground;
}

.main-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
}

.modal-textarea {
  @apply min-h-[110px] resize-y;
}

.media-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
}

.media-upload-card {
  @apply rounded-md border bg-muted/30 p-3;
}

.participants-header {
  @apply mb-3 flex flex-wrap items-center justify-between gap-2;
}

.participants-empty {
  @apply rounded-md border border-dashed bg-muted/40 px-3 py-2 text-sm text-muted-foreground;
}

.participant-row {
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr;
  align-items: end;
}

.participant-row + .participant-row {
  margin-top: 8px;
}

.participant-user-select {
  @apply h-9;
}

.modal-actions {
  @apply sticky bottom-0 z-10 flex justify-end gap-2 border-t bg-card pt-4;
}

:deep(.media-upload-card .fileupload) {
  margin: 0;
  max-width: none;
  width: 100%;
}

:deep(.media-upload-card .upload-label) {
  margin-bottom: 6px;
}

@media (min-width: 960px) {
  .main-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .media-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .participant-row {
    grid-template-columns: minmax(220px, 1fr) minmax(240px, 1fr) auto;
  }
}
</style>
